import {css} from 'aphrodite';
import React, {useCallback} from 'react';

import {runAnalysisStep} from 'analysis-tool/AnalysisActions';
import {getArchivedTooltipMessage} from 'analysis-tool/AnalysisToolMessages';
import {isNonRefreshableStepType} from 'analysis-tool/AnalysisTypes';
import {CustomDataViewButton} from 'analysis-tool/data-view/DataViewContext';
import {dataViewToolbarStyles} from 'analysis-tool/data-view/DataViewToolbar';
import {isNonReRunnableStepType} from 'analysis-tool/EditStepHelpers';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {recordAnalysisAction} from 'analysis-tool/ui/debugAnalysisActionHistory';
import {trackEvent} from 'analytics/Analytics';
import isArchived from 'archive/isArchived';
import {ANALYSIS_TOOL_ANALYTICS_ACTIONS, RERUN_SOURCE} from 'constants/AnalyticsConstants';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL, PipelineStep, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {resolveLineageStep} from 'pipelines/PipelineViewUtils';
import {getInputStepDatas} from 'pipelines/StepConnectionUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {withLoadingScreen} from 'util/Promises';
interface RerunStepButtonProps {
  analysisId: string;
  stepId: string;
  stepType: PipelineStepType;
  isStepRunning: boolean;
  isAnalysisArchived: boolean;
}

/** @package Exported for tests */
export function RerunStepButton({
  analysisId,
  stepId,
  stepType,
  isStepRunning,
  isAnalysisArchived,
}: RerunStepButtonProps): JSX.Element {
  const isDisabled = isAnalysisArchived || isStepRunning;

  const helpText = isAnalysisArchived
    ? getArchivedTooltipMessage({id: analysisId})
    : isStepRunning
      ? 'Step is currently running'
      : 'Rerun step';

  const handleClick = useCallback(async () => {
    recordAnalysisAction('RERUN_STEP', {
      analysisId,
      stepId,
      stepType,
    });
    trackEvent(ANALYSIS_TOOL_ANALYTICS_ACTIONS.TRIGGERED_STEP_RERUN, {
      rerun_source: RERUN_SOURCE.TABLE,
      step_id: stepId,
      step_type: stepType,
      pipeline_id: analysisId,
    });
    await withLoadingScreen(runAnalysisStep(analysisId, {stepId}), 'Rerunning step…');
  }, [analysisId, stepId, stepType]);

  return (
    <Tooltip text={helpText}>
      <Button
        aria-label="Rerun step"
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        className={css(dataViewToolbarStyles.button)}
        disabled={isDisabled}
        icon="rotate-right"
        onClick={isDisabled ? undefined : handleClick}
        size={ButtonSize.MINI}
      />
    </Tooltip>
  );
}

/** @package Exported for tests */
export function canRefreshView(view: PipelineViewGraphQL, steps: ReadonlyArray<PipelineStep>): boolean {
  if (view.lineage.length === 0) {
    return false;
  }

  const sourceStep = resolveLineageStep(steps, view.lineage[0]);
  const sourceStepType = sourceStep.stepType;

  if (getInputStepDatas(sourceStepType).length > 0) {
    return false;
  }

  if (isNonReRunnableStepType(sourceStepType) || isNonRefreshableStepType(sourceStepType)) {
    return false;
  }

  return true;
}

export default function getRerunStepAction(
  analysis: PipelineGraphQL,
  view: PipelineViewGraphQL
): CustomDataViewButton {
  if (!canRefreshView(view, analysis.steps) || isAnalysisReadOnly(analysis)) {
    return null;
  }

  const firstStep = resolveLineageStep(analysis.steps, view.lineage[0]);
  return (
    <RerunStepButton
      analysisId={analysis.id}
      stepId={firstStep.id}
      stepType={firstStep.stepType}
      isStepRunning={firstStep.statusCode === PipelineStepStatusCode.RUNNING}
      isAnalysisArchived={isArchived(analysis)}
    />
  );
}
